import { useState } from 'react'
import PropTypes from 'prop-types'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { formatInTimeZone } from 'date-fns-tz'
import { useTranslation } from 'react-i18next'

import { getBuildingTimezoneFromEvent } from '../../helpers/helpers'
import { getAssetLink } from '../../helpers/get-asset-link'
import API from '../../api/api'
import { setUserData } from '../../reducers/userDataReducer'

import Icon from './../Icons'

const CalendarPopup = ({ trainings, onHidePopup, onUpdate }) => {
  const { t } = useTranslation(['calendar'])
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const userData = useSelector((state) => state.userData)

  const [now] = useState(() => Date.now())

  const handleEventOrder = (e, training) => {
    if (training.signUpLink) {
      window.open(training.signUpLink, '_blank')
    } else {
      e.preventDefault()
      e.target.closest('.ctp-classbar__action')?.classList.add('ctp-classbar__action--inactive')
      onUpdate('training-details', training)
    }
  }

  const handleOrderPersonalTraining = (e) => {
    e.preventDefault()
    if (userData.tag === 'Paying') {
      navigate('/dashboard/store')
    } else {
      const newUserData = { ...userData, tag: 'Prospect' }
      API.switchClientToProspect(userData.user.id)
        .then(() => {
          e.target.closest('.ctp-classbar__action')?.classList.add('ctp-classbar__action--inactive')
          onUpdate('order-personal-training-popup')
          dispatch(setUserData(newUserData))
        })
        .catch((error) => {
          console.error('Error updating user:', error)
        })
    }
  }

  const getTrainingType = (training) => {
    if (training.cost === 0) {
      return training.buildingId !== null ? 'class' : 'appointment'
    }
    return 'event'
  }

  const renderAction = (event) => {
    const renderPriceText = (price) => {
      if (event.canceled) return t('calendar:widgetPopup.status.canceled')
      if (getTrainingType(event) === 'appointment' || event.signed)
        return t('calendar:widgetPopup.status.booked')
      return price > 0
        ? t('calendar:widgetPopup.fromPrice', { price })
        : t('calendar:widgetPopup.free')
    }

    let classes = 'ctp-classbar__action'
    if (event.canceled || event.date <= now) {
      classes += ' ctp-classbar__action--inactive'
    }

    const text = event.canceled
      ? t('calendar:widgetPopup.status.canceled')
      : event.signed
        ? t('common:actions.cancel')
        : t('calendar:widgetPopup.join')

    return (
      <div className={classes}>
        <span>{renderPriceText(event.cost)}</span>
        <span onClick={(e) => !event.canceled && handleEventOrder(e, event)}>{text}</span>
      </div>
    )
  }

  return (
    <div className="ctp-popup">
      <div className="ctp-popup__header">
        <div className="ctp-popup__cell">
          <button onClick={onHidePopup} className="ctp-popup__close">
            <Icon icon="cross" />
          </button>
        </div>
        <div className="ctp-popup__cell">
          <span>{t('calendar:widgetPopup.event')}</span>
        </div>
        <div className="ctp-popup__cell">
          <span>{t('calendar:widgetPopup.staff')}</span>
        </div>
        <div className="ctp-popup__cell">
          <span>{t('calendar:widgetPopup.rsvp')}</span>
        </div>
        <div className="ctp-popup__cell">
          <span>{t('calendar:widgetPopup.time')}</span>
        </div>
        <div className="ctp-popup__cell">
          <span>{t('calendar:widgetPopup.price')}</span>
        </div>
      </div>
      <div className="ctp-popup__inner">
        {trainings.map((e, i) => {
          const timezone = getBuildingTimezoneFromEvent(e)
          const startTime = formatInTimeZone(e.date, timezone, 'p')

          return (
            <div className="ctp-classbar" key={i}>
              <div className="ctp-classbar__cell">
                <div className="ctp-classbar__icon" style={{ backgroundColor: e.type.color }}>
                  <Icon icon={e.type.icon} />
                </div>
              </div>
              <div className="ctp-classbar__cell">
                <span>{e.name}</span>
              </div>
              <div className="ctp-classbar__cell">
                <div className="ctp-classbar__avatar">
                  <img
                    src={getAssetLink({ path: 'chitown-avatar/avatar', file: e.trainerId.avatar })}
                    alt="Trainer"
                  />
                </div>
                <span>{e.trainerId.firstName}</span>
              </div>
              <div className="ctp-classbar__cell">
                <span>
                  {getTrainingType(e) === 'appointment' ? e.capacity : e.taken}/{e.capacity}
                </span>
              </div>
              <div className="ctp-classbar__cell">
                <span>{startTime}</span>
              </div>
              {getTrainingType(e) !== 'reminder' && (
                <div className="ctp-classbar__cell">{renderAction(e)}</div>
              )}
            </div>
          )
        })}
      </div>
      {trainings.length === 0 && (
        <div className="ctp-popup__no-activities">
          <p>{t('calendar:widgetPopup.noActivities')}</p>
        </div>
      )}
      {userData.tag !== 'Prospect' && (
        <div className="ctp-popup__footer">
          <p>{t('calendar:widgetPopup.personalTrainingCta')}</p>
          <button className="button button-primary" onClick={handleOrderPersonalTraining}>
            {t('calendar:widgetPopup.purchasePersonalTraining')}
          </button>
        </div>
      )}
      <span className="ctp-popup__arrow" />
    </div>
  )
}

CalendarPopup.propTypes = {
  trainings: PropTypes.array.isRequired,
  onHidePopup: PropTypes.func.isRequired,
  onUpdate: PropTypes.func.isRequired,
}

export default CalendarPopup
